'use client';
import React from 'react';
import sanitizeHtml from 'sanitize-html';
import BenefitsHeader from './components/BenefitsHeader';
import BenefitImage from './components/BenefitImage';
import BenefitDescription from './components/BenefitDescription';
import IndexBasedStepSelector from '../../shared/IndexBasedStepSelector';
import { useIndexSelection } from '@/hooks/useIndexSelection';
import ImmediacyImage from '../../../../assets/benefits/Immediacy.svg';
import AccuracyImage from '../../../../assets/benefits/Accuracy.svg';
import PrivacyImage from '../../../../assets/benefits/Privacy.svg';
import SupportImage from '../../../../assets/benefits/Support.svg';

const sanitizeAndRenderHtml = (
  content: string | undefined
): { __html: string } | undefined => {
  if (!content) return undefined;

  const sanitized = sanitizeHtml(content, {
    allowedAttributes: {
      span: ['class'],
    },
    allowedClasses: {
      span: ['*'], // Allow all classes on span elements
    },
    allowedTags: ['b', 'strong', 'i', 'em', 'u', 'span', 'br', 'p'],
  });

  return { __html: sanitized };
};

interface Benefit {
  name: string;
  description: string;
  imageUrl?: string;
  imageAlt?: string;
}

const BenefitsSection = ({
  benefits = [
    {
      description:
        "Purchase a kit and it'll arrive at your door in mere days. Your rapid test provides results in 60 seconds-faster than it takes to re-download your go-to dating app.",
      imageAlt: 'Illustration representing Immediacy benefit',
      imageUrl: ImmediacyImage,
      name: 'Immediacy',
    },
    {
      description:
        'Our trusti™ HIV Testing Kit uses the same rapid test tech as Ontario clinics, just packaged for your bathroom counter. When done right, your results are 99%+ accurate.',
      imageAlt: 'Illustration representing Accuracy benefit',
      imageUrl: AccuracyImage,
      name: 'Accuracy',
    },
    {
      description:
        "Plain packaging, discreet billing, and a secure results portal that's locked down tight. Best part? You can test in your seats (or nothing at all, we don't judge).",
      imageAlt: 'Illustration representing Privacy benefit',
      imageUrl: PrivacyImage,
      name: 'Privacy',
    },
    {
      description:
        "Testing can feel overwhelming, even if it's not your first rodeo. That's why our nurses are here for every step, whether that's walking you through the test or connecting you with further care. Our nurses skip the pearl-clutching and stick to what matters: compassionate support and the care you need.",
      imageAlt: 'Illustration representing Support benefit',
      imageUrl: SupportImage,
      name: 'Support',
    },
  ],
  sectionHeading = 'Benefits',
}: {
  sectionHeading?: string;
  benefits?: Benefit[];
}) => {
  const { selectedIndex, selectedItem, setSelectedIndex } =
    useIndexSelection(benefits);

  return (
    <section className="my-4 flex w-full flex-col items-center justify-center px-8 py-[48px] md:py-[24px]">
      <div className="content-wrapper flex w-full items-center justify-center pb-8">
        <div className="flex w-full flex-col items-center">
          <BenefitsHeader
            selectedBenefitName={selectedItem?.name}
            sectionHeading={sectionHeading}
          />
          <BenefitImage selectedBenefit={selectedItem} />
          <BenefitDescription>
            <span
              dangerouslySetInnerHTML={sanitizeAndRenderHtml(
                selectedItem?.description
              )}
            />
          </BenefitDescription>
          <IndexBasedStepSelector
            items={benefits}
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
            getItemLabel={benefit => benefit.name}
            theme="benefits"
          />
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
